import React, { useEffect, useState } from 'react';
import {
    FlatList,
    HStack,
    Link,
    VStack,
} from 'native-base';
import { Caption, H2, H3, H4 } from '~/shared/Typography';
import { SpIcon } from '~/shared/Icon';
import { faClock } from '@fortawesome/free-solid-svg-icons';
import { SBDropdown } from '~/shared/SBDropdown';
import { FullfillmentOptions } from './FullfillmentOptions';
import { isBefore, isAfter, format, parse } from 'date-fns';
import {
    useDutchieStore,
    Hours,
    HoursDay,
    Maybe,
    OrderType,
    FulfillmentOptionsCardProp,
    useTheme,
} from '@springbig/shared';
import { ActionSheet, Button, } from '~/shared/Theme';

type ShopStoreBottomSheetProps = {
    isOpen: boolean;
    onClose: () => void;
};

type HoursCardProp = {
    day: string;
    start?: string;
    end?: string;
};

export const ShopStoreBottomSheet = ({
    isOpen,
    onClose
}: ShopStoreBottomSheetProps) => {

    const {
        button_primary,
        button_text,
        primary,
        radius_sm,
    } = useTheme();

    const { retailer } = useDutchieStore();

    const [hours, setHours] =
        useState<Array<HoursCardProp>>([]);

    const [isStoreOpen, setIsStoreOpen] =
        useState<boolean>(false);

    const [fullfillmentOptions, setFullfillmentOptions] =
        useState<Array<FulfillmentOptionsCardProp>>([]);

    const [fullfillmentOption, setFullfillmentOption] =
        useState();

    const getHoursCard = (day: string, dayHours: Hours) : HoursCardProp => {

        let hoursCard : HoursCardProp = { day };

        if (dayHours) {
            const dayHour = (dayHours[day as keyof Hours]) as HoursDay;
            if (dayHour?.active && dayHour?.start && dayHour?.end) {
                hoursCard = { day, start: dayHour?.start, end: dayHour?.end };
            }
        }

        return hoursCard;
    };

    useEffect(() => {

        if (fullfillmentOption && fullfillmentOption?.hoursKey) {
            const fullfillmentOptionHours : Maybe<Hours> = retailer?.hours[fullfillmentOption.hoursKey];
            const dayOfTheWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
            if (fullfillmentOptionHours) {
                const hoursCard = dayOfTheWeek.map((e) => getHoursCard(e, fullfillmentOptionHours));
                setHours(hoursCard);
            }
        }

    }, [fullfillmentOption]);

    const storeSchedule = (): boolean => {

        const currentHours = retailer?.hours[fullfillmentOption.hoursKey];
        const currentDayOfTheWeek = format(new Date(), 'EEEE');
        const todayHours = currentHours[currentDayOfTheWeek];
        const startHour = parse(todayHours.start, 'h:mm a', new Date());
        const endHour = parse(todayHours.end, 'h:mm a', new Date());

        return todayHours.active && isAfter(new Date(), startHour) && isBefore(new Date(), endHour);
    };

    useEffect(() => {

        if (retailer && fullfillmentOption) {
            setIsStoreOpen(storeSchedule());
        }
    }, [retailer, fullfillmentOption]);

    const colors = {
        open: '#5eb14b',
        close: '#c42f3b',
    };

    useEffect(() => {

        const availableFulfillmentOptions: Array<FulfillmentOptionsCardProp> = [];
        if (retailer?.fulfillmentOptions.pickup) {
            availableFulfillmentOptions.push({ key: OrderType.Pickup, label: 'pickup', hoursKey: 'pickup' });
        }

        if (retailer?.fulfillmentOptions.curbsidePickup) {
            availableFulfillmentOptions.push({ key: OrderType.CurbsidePickup, label: 'curbside pickup', hoursKey: 'curbsidePickup' });
        }

        if (retailer?.fulfillmentOptions.driveThruPickup) {
            availableFulfillmentOptions.push({ key: OrderType.DriveThruPickup, label: 'drive thru pickup', hoursKey: 'driveThruPickup' });
        }

        if (retailer?.fulfillmentOptions.delivery) {
            availableFulfillmentOptions.push({ key: OrderType.Delivery, label: 'delivery', hoursKey: 'delivery' });
        }

        if (fullfillmentOption) {
            setFullfillmentOption(fullfillmentOption);
        }
        else if (availableFulfillmentOptions && availableFulfillmentOptions[0]) {
            setFullfillmentOption(availableFulfillmentOptions[0]);
        }

        setFullfillmentOptions(availableFulfillmentOptions);
    }, [retailer]);

    return (
        <ActionSheet isOpen={isOpen} onClose={onClose} >
            { retailer &&
                    <VStack w={'100%'} alignItems={'center'} space={8}>
                        <H2>
                            { retailer?.name }
                        </H2>
                        <HStack space={2} w={'100%'} alignItems={'center'} justifyContent={'space-evenly'}>
                            { retailer.phone &&
                                <Link
                                    bg={button_primary}
                                    rounded={radius_sm}
                                    p={1}
                                    px={4}
                                    w={'100px'}
                                    justifyContent={'center'}
                                    isUnderlined={false}
                                    href={`tel:${retailer.phone}`}
                                >
                                    <Caption
                                        text={'shop.call'}
                                        color={button_text}
                                    />
                                </Link>
                            }
                            { retailer.address &&
                                <Button
                                    text={'shop.directions'}
                                    modifier={'small'}
                                    w={'100px'}
                                    onPress={() => {

                                        window.open(`https://www.google.com/maps?q=${retailer.address}`);
                                    }}
                                />
                            }
                        </HStack>
                        <SBDropdown value={fullfillmentOption?.label}>
                            <FullfillmentOptions
                                setFullfillmentOption={setFullfillmentOption}
                                fullfillmentOptions={fullfillmentOptions}
                                selectedFulfillmentOption={fullfillmentOption}
                            />
                        </SBDropdown>
                        <HStack space={2} justifyContent={'center'}>
                            <SpIcon color={isStoreOpen ? colors.open : colors.close} icon={faClock} />
                            <H4 color={isStoreOpen ? colors.open : colors.close}>
                                { isStoreOpen ? 'Open' : 'Close' }
                            </H4>
                        </HStack>
                        <FlatList
                            width={'100%'}
                            data={hours}
                            renderItem={({ item } : { item: HoursCardProp }) =>

                                <HStack
                                    p={2}
                                    w={'100%'}
                                    alignItems={'center'}
                                    justifyContent={'space-between'}
                                    borderBottomWidth={1}
                                    borderBottomColor={primary}
                                >
                                    <H3 fontWeight={'300'}>
                                        { item.day }
                                    </H3>
                                    { item.start && item.end ?
                                        <H3 fontWeight={'300'}>
                                            { item.start } - { item.end }
                                        </H3>
                                        :
                                        <H3 fontWeight={'300'}>
                                          Closed
                                        </H3>
                                    }
                                </HStack>
                            }
                        />
                    </VStack>
            }
        </ActionSheet>
    );
};
